import useTranslation from '../useTranslation';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import useChemical from 'hooks/useChemical';
import type { EnrichedChemical } from 'hooks/useChemical/types';
import usePageRoutes from 'hooks/usePageRoutes';

const useData = () => {
    const { chemicalId } = useParams();
    const { fetchChemical, isLoading } = useChemical();
    const { chemicalsPage } = usePageRoutes();
    const navigate = useNavigate();
    const { REVIEW_FORMULATION } = useTranslation();
    const [chemical, setChemical] = useState<EnrichedChemical | undefined>(undefined);
    const [isFormulationReviewOpen, setIsFormulationReviewOpen] = useState(false);

    const loadChemical = useCallback(async () => {
        if (chemicalId) {
            const result = await fetchChemical({ id: chemicalId });

            setChemical(result ?? undefined);
        }
    }, [chemicalId, fetchChemical]);

    useEffect(() => {
        loadChemical();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chemicalId]);

    const dropdownMenuItems = [
        {
            isDisabled: !chemical,
            onClick: () => setIsFormulationReviewOpen(true),
            text: REVIEW_FORMULATION
        }
    ];

    const handleFormulationReviewClose = () => setIsFormulationReviewOpen(false);

    const handleClose = () => {
        navigate(chemicalsPage);
    };

    const classifications = chemical?.classifications ?? [];
    const primaryClassification = classifications.find(item => item.primary)?.name ?? '-';
    const formulationName = chemical?.formulationName ?? '-';
    const formulationCode = chemical?.formulationCode;
    const formulation = formulationCode
        ? `${formulationName} (${formulationCode})`
        : formulationName;
    const reiMin = chemical?.reiMin;
    const reiMax = chemical?.reiMax;
    const reiUnit = chemical?.reiUnit ?? '';
    const rei = reiMin !== null && reiMin !== undefined ? `${reiMin}-${reiMax} ${reiUnit}` : '-';
    const rainfastnessHours = chemical?.rainfastnessHours;
    const rainfastness =
        rainfastnessHours !== null && rainfastnessHours !== undefined
            ? String(rainfastnessHours)
            : '-';
    const modeOfAction = chemical?.modeOfActions?.join(', ') ?? '-';

    return {
        chemical,
        dropdownMenuItems,
        formulation,
        handleClose,
        handleFormulationChanged: loadChemical,
        handleFormulationReviewClose,
        isFormulationReviewOpen,
        isLoading,
        modeOfAction,
        primaryClassification,
        rainfastness,
        rei
    };
};

export default useData;
